/**
 * App Component
 * 
 * Main application component with Auth0 authentication integration.
 * Handles authentication states and routes between login and dashboard.
 */

import React from 'react';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import AuthenticatedApp from './AuthenticatedApp';
import LoginPage from './pages/LoginPage';
import { SettingsProvider } from './contexts/SettingsContext';

function LoadingSpinner() {
  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3AD0E6]"></div>
    </div>
  );
}

function AppContent() {
  const { isLoading, isAuthenticated, error } = useAuth0();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center text-white">
          <h1 className="text-2xl font-bold mb-4">Authentication Error</h1>
          <p className="text-gray-400">{error.message}</p>
          {/* A plain link to the root drops Auth0's ?error=&state= params, which
              otherwise fail again with "Invalid state" on every refresh. */}
          <a
            href="/"
            className="inline-block mt-6 px-4 py-2 rounded-md bg-gray-700 text-white hover:bg-gray-600"
          >
            Back to sign in
          </a>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return <AuthenticatedApp />;
}

function App() {
  // Validate Auth0 configuration
  const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN;
  const auth0ClientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
  const isAuth0Configured = auth0Domain && auth0ClientId;

  // If Auth0 is not configured, show login page with configuration warning
  if (!isAuth0Configured) {
    return <LoginPage isAuth0Configured={false} />;
  }

  return (
    <Auth0Provider
      domain={auth0Domain}
      clientId={auth0ClientId}
      cacheLocation="localstorage"
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: 'openid profile email'
      }}
    >
      <SettingsProvider>
        <AppContent />
      </SettingsProvider>
    </Auth0Provider>
  );
}

export default App;
